/**
 * themeFormatters — locale & unit-aware formatting helpers for SiteRAG
 * reports. Consumed by report-body renderers so the same backend SI
 * value renders differently per `theme.themeLocale` (TSE-2 C4).
 *
 * Backend convention (CLAUDE.md PROJECT CONVENTIONS):
 *   - Temperatures stored in °C.
 *   - Distances stored in metres.
 *   - Energy stored in kWh (or its multiples kW × hours).
 *   - Dates stored as ISO 8601 strings.
 *
 * Conversion happens at the FORMATTER BOUNDARY, NOT inside numeric
 * pipelines. Identical to fluid_backend.py's CoolProp-conversion-at-
 * boundary discipline.
 *
 * All functions are pure / synchronous / no Intl-time-zone reliance.
 */

import type { ReportTheme } from '../types/reportTheme';

export type ThemeLocale = NonNullable<ReportTheme['themeLocale']>;

const DEFAULT_LOCALE: Required<ThemeLocale> = {
  dateFormat: 'YMD',
  numberFormat: '1,234.56',
  temperatureUnit: 'C',
  distanceUnit: 'metric',
  energyUnit: 'kWh-MWh',
};

function fill(locale: ThemeLocale | undefined): Required<ThemeLocale> {
  return { ...DEFAULT_LOCALE, ...(locale ?? {}) };
}

/* ───────────────────────────────────────────────────────────────────
 * Numbers
 * ─────────────────────────────────────────────────────────────────── */

/**
 * Format a number using the locale's grouping/decimal separators.
 * Always returns 2 decimal places when the input is fractional;
 * trims trailing zeros for integers so `formatNumber(5)` → `"5"`,
 * not `"5.00"`.
 */
export function formatNumber(n: number, locale?: ThemeLocale): string {
  if (!Number.isFinite(n)) return String(n);
  const fmt = fill(locale).numberFormat;
  // Split sign + integer + fraction parts manually so we don't depend
  // on Intl.NumberFormat (avoids ICU locale availability variance).
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const isInt = Number.isInteger(abs);
  const fixed = isInt ? abs.toFixed(0) : abs.toFixed(2);
  const [intRaw, fracRaw] = fixed.split('.');
  const intGrouped = groupDigits(intRaw, fmt);
  if (!fracRaw) return sign + intGrouped;
  const decSep = decimalSeparator(fmt);
  return sign + intGrouped + decSep + fracRaw;
}

function groupSeparator(fmt: ThemeLocale['numberFormat']): string {
  switch (fmt) {
    case '1.234,56': return '.';
    case '1 234.56': return ' ';
    case '1,234.56':
    default: return ',';
  }
}

function decimalSeparator(fmt: ThemeLocale['numberFormat']): string {
  switch (fmt) {
    case '1.234,56': return ',';
    case '1 234.56':
    case '1,234.56':
    default: return '.';
  }
}

function groupDigits(intStr: string, fmt: ThemeLocale['numberFormat']): string {
  const sep = groupSeparator(fmt);
  // Insert separator every 3 digits from the right.
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

/* ───────────────────────────────────────────────────────────────────
 * Dates
 * ─────────────────────────────────────────────────────────────────── */

/**
 * Render an ISO date string in the user's preferred format.
 *  - DMY  → `17/05/2026`
 *  - MDY  → `05/17/2026`
 *  - YMD  → `2026-05-17`
 *  - ISO  → original ISO (no truncation; safe for full timestamps)
 *
 * Returns the input unchanged when it cannot be parsed (invalid ISO).
 */
export function formatDate(iso: string, locale?: ThemeLocale): string {
  if (!iso) return iso;
  const fmt = fill(locale).dateFormat;
  if (fmt === 'ISO') return iso;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const y = String(date.getUTCFullYear());
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  switch (fmt) {
    case 'DMY': return `${d}/${m}/${y}`;
    case 'MDY': return `${m}/${d}/${y}`;
    case 'YMD':
    default:    return `${y}-${m}-${d}`;
  }
}

/* ───────────────────────────────────────────────────────────────────
 * Temperature
 * ─────────────────────────────────────────────────────────────────── */

/** Convert and label a Celsius value per the locale's unit preference. */
export function formatTemperature(celsius: number, locale?: ThemeLocale): string {
  if (!Number.isFinite(celsius)) return String(celsius);
  const unit = fill(locale).temperatureUnit;
  if (unit === 'F') {
    // °F = °C × 9/5 + 32 — exact within float precision.
    const f = celsius * 9 / 5 + 32;
    return `${formatNumber(f, locale)}°F`;
  }
  return `${formatNumber(celsius, locale)}°C`;
}

/* ───────────────────────────────────────────────────────────────────
 * Distance
 * ─────────────────────────────────────────────────────────────────── */

const M_PER_KM = 1000;
const M_PER_FT = 0.3048;
const M_PER_MI = 1609.344;

/**
 * Format a length (in metres) using the locale's distance unit.
 * Picks the natural human-readable scale automatically:
 *   - metric:   < 1km → metres,    ≥ 1km → kilometres
 *   - imperial: < 1mi → feet,      ≥ 1mi → miles
 */
export function formatDistance(meters: number, locale?: ThemeLocale): string {
  if (!Number.isFinite(meters)) return String(meters);
  const unit = fill(locale).distanceUnit;
  if (unit === 'imperial') {
    const miles = meters / M_PER_MI;
    if (Math.abs(miles) >= 1) return `${formatNumber(miles, locale)} mi`;
    const feet = meters / M_PER_FT;
    return `${formatNumber(feet, locale)} ft`;
  }
  // metric
  if (Math.abs(meters) >= M_PER_KM) return `${formatNumber(meters / M_PER_KM, locale)} km`;
  return `${formatNumber(meters, locale)} m`;
}

/* ───────────────────────────────────────────────────────────────────
 * Energy
 * ─────────────────────────────────────────────────────────────────── */

/**
 * Format an energy quantity stored in kWh using the locale's preferred
 * scale label. With `kWh-MWh`: small values stay in kWh; values ≥
 * 1000 are promoted to MWh. With `GWh`: every value is divided by 1e6
 * (small values render as fractions). Backend storage is always SI.
 */
export function formatEnergy(kWh: number, locale?: ThemeLocale): string {
  if (!Number.isFinite(kWh)) return String(kWh);
  const unit = fill(locale).energyUnit;
  if (unit === 'GWh') {
    const gwh = kWh / 1_000_000;
    return `${formatNumber(gwh, locale)} GWh`;
  }
  // kWh-MWh
  if (Math.abs(kWh) >= 1000) {
    const mwh = kWh / 1000;
    return `${formatNumber(mwh, locale)} MWh`;
  }
  return `${formatNumber(kWh, locale)} kWh`;
}
